import React, { useEffect, useRef, useState } from 'react';

import BOARD_IMAGE from '../assets/telegram_board.png';
import BOT_AVATAR from '../assets/logo_sign.png';

const USERNAME = "Chess Enjoyer";

const STEPS = {
  EMPTY: 0,
  USER: 1,
  ANALYZING: 2,
  REPLY: 3,
};

const TelegramChatDemo = () => {
  const [step, setStep] = useState(STEPS.EMPTY);
  const timer = useRef(null);

  useEffect(() => {
    if (timer) {
      clearTimeout(timer);
    }

    if (step === STEPS.EMPTY) {
      timer.current = setTimeout(() => setStep(STEPS.USER), 1000);
    } else if (step === STEPS.USER) {
      timer.current = setTimeout(() => setStep(STEPS.ANALYZING), 1500);
    } else if (step === STEPS.ANALYZING) {
      timer.current = setTimeout(() => setStep(STEPS.REPLY), 2500);
    } else if (step === STEPS.REPLY) {
      timer.current = setTimeout(() => setStep(STEPS.EMPTY), 7500);
    }
    return () => timer.current && clearTimeout(timer)
  }, [step]);

  return (
    <div style={styles.chat}>
      {step >= STEPS.USER && <UserMessage />}

      {step >= STEPS.ANALYZING && (
        <BotMessage analyzing={step === STEPS.ANALYZING} />
      )}
    </div>
  );
};

const BoardImage = ({ small = false }) => (
  <img
    src={BOARD_IMAGE}
    alt="Chess diagram"
    style={{
      width: small ? 32 : 315,
      height: small ? 32 : 315,
      objectFit: 'cover',
      borderRadius: small ? 3 : 2,
      border: small ? 'none' : '4px solid #6f2326',
      boxShadow: small ? 'none' : '0 2px 8px rgba(0,0,0,0.25)',
    }}
  />
);

const BotAvatarImage = () => (
  <img
    src={BOT_AVATAR}
    alt="bot"
    style={{
      width: 32,
      height: 32,
      borderRadius: '50%',
    }}
  />
);

const UserMessage = () => (
  <div style={styles.messageRow}>
    <div style={styles.userAvatar}>{USERNAME[0].toUpperCase()}</div>

    <div style={{ animation: 'fadeIn 300ms ease' }}>
      <div style={styles.userName}>{USERNAME}</div>

      <BoardImage />

      <div style={styles.command}>
        <span style={styles.slash}>/scan</span> white
      </div>
    </div>
  </div>
);

const BotMessage = ({ analyzing }) => (
  <div style={styles.messageRow}>
    <div style={styles.botAvatar}>
      <BotAvatarImage />
    </div>

    <div style={styles.botContent}>
      <div style={styles.botName}>ChessvisionAiBot</div>

      <div style={styles.quote}>
        <BoardImage small />

        <div>
          <div style={styles.quoteName}>{USERNAME}</div>
          <div style={styles.quoteText}>/scan white</div>
        </div>
      </div>

      {analyzing ? <Analyzing /> : <FinalReply />}
    </div>
  </div>
);

const Analyzing = () => (
  <div style={{ ...styles.text, animation: 'fadeIn 200ms ease' }}>
    🤖 Analyzing image<span style={styles.dots}>...</span>
  </div>
);

const FinalReply = () => (
  <div style={{ animation: 'fadeIn 250ms ease' }}>
    <p style={styles.text}>
      I analyzed the image and this is what I see.
    </p>

    <p style={styles.text}>
      Analyze on: <span style={styles.link}>chessvision.ai</span> |{' '}
      <span style={styles.link}>chess.com</span>
    </p>

    <p style={styles.text}>
      Found <span style={styles.link}>many videos</span> with this position.
    </p>

    <p style={styles.text}>
      From game{' '}
      <span style={styles.link}>
        Magnus Carlsen vs. Sergey Karjakin, 2016
      </span>
    </p>

    <div style={styles.buttonGrid}>
      <button style={styles.button}>Eval</button>
      <button style={styles.button}>Hint</button>
      <button style={styles.button}>Best line</button>
    </div>

    <button style={styles.fullButton}>
      Analyze on Chessvision.ai ↗
    </button>

    <button style={styles.fullButton}>
      Save to Library
    </button>
  </div>
);

const styles = {
  chat: {
    width: '100%',
    maxWidth: 520,
    minHeight: 680,
    margin: '0 auto',
    padding: 18,
    backgroundColor: '#17212b',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
    borderRadius: 10,
    boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
    textAlign: 'left',
    minHeight: 790,
  },

  messageRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },

  userAvatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    backgroundColor: '#64b5f6',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
  },

  botAvatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  userName: {
    color: '#2da8ff',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
  },

  botName: {
    color: '#2da8ff',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
  },

  command: {
    marginTop: 8,
    fontSize: 14,
  },

  slash: {
    color: '#2da8ff',
    fontWeight: 700,
  },

  botContent: {
    maxWidth: 326,
  },

  quote: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#1f3142',
    borderRadius: 4,
    padding: '5px 8px',
    marginBottom: 8,
  },

  quoteName: {
    color: '#40a7ff',
    fontSize: 13,
    fontWeight: 700,
  },

  quoteText: {
    fontSize: 13,
    fontWeight: 700,
  },

  text: {
    fontSize: 13,
    marginBottom: 12,
  },

  dots: {
    letterSpacing: 2,
  },

  link: {
    color: '#2da8ff',
  },

  buttonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 4,
  },

  button: {
    backgroundColor: '#213142',
    color: '#fff',
    border: 0,
    padding: '9px 6px',
    fontWeight: 700,
    fontSize: 12,
    borderRadius: 4,
  },

  fullButton: {
    width: '100%',
    backgroundColor: '#213142',
    color: '#fff',
    border: 0,
    marginTop: 6,
    padding: '10px 8px',
    borderRadius: 4,
    fontWeight: 700,
    fontSize: 12,
  },
};

export default TelegramChatDemo;